/**
 * VAPID helpers — Family
 *
 * Web Push uses an `applicationServerKey` formatted as a `Uint8Array`, but the
 * server publishes the public key as a base64url string (RFC 7515 §2). This
 * module converts between the two.
 *
 * The conversion lives in its own file so it can be unit-tested without touching
 * the browser-only `PushManager` API.
 *
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/PushManager/subscribe
 */

/**
 * Convert a base64url-encoded VAPID public key to a `Uint8Array<ArrayBuffer>`.
 *
 * Returns a `Uint8Array` whose backing buffer is a fresh `ArrayBuffer` (never a
 * `SharedArrayBuffer`), so the result is directly assignable to
 * `PushSubscriptionOptionsInit.applicationServerKey` without TS friction.
 *
 * Throws `Error` on malformed input so callers can guard at the surface.
 *
 * @param base64UrlString base64url string (no padding, '-' and '_' instead of '+' and '/')
 */
export function urlBase64ToUint8Array(base64UrlString: string): Uint8Array<ArrayBuffer> {
  if (typeof base64UrlString !== 'string' || base64UrlString.length === 0) {
    throw new Error('VAPID public key is empty.');
  }

  // Pad the string to a multiple of 4 (required for atob)
  const padding = '='.repeat((4 - (base64UrlString.length % 4)) % 4);
  // Convert URL-safe alphabet ('-_') back to standard base64 alphabet ('+/')
  const base64 = (base64UrlString + padding).replace(/-/g, '+').replace(/_/g, '/');

  let raw: string;
  try {
    raw = atob(base64);
  } catch {
    throw new Error('VAPID public key is not valid base64.');
  }

  // Allocate a non-shared ArrayBuffer up-front so the returned view's `.buffer`
  // is provably an ArrayBuffer (not ArrayBufferLike). Required by lib.dom for
  // PushSubscriptionOptionsInit.applicationServerKey under strict typing.
  const buffer = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/**
 * Convert an `ArrayBuffer` / `ArrayBufferView` (e.g. `subscription.getKey('p256dh')`)
 * to a base64url string suitable for storing in `push_subscriptions.keys` JSON.
 *
 * Returns `null` for null/undefined input so callers can pipe getKey() output
 * directly without an extra null-check.
 *
 * Accepts both ArrayBuffer and SharedArrayBuffer-backed views (ArrayBufferLike)
 * because `Uint8Array.buffer` is typed as ArrayBufferLike under TS 5.7+.
 */
export function arrayBufferToBase64Url(buffer: ArrayBufferLike | null): string | null {
  if (!buffer) return null;
  // We only read; SharedArrayBuffer is read-safe so the cast through the
  // ArrayBufferLike contract is sound.
  const bytes = new Uint8Array(buffer as ArrayBuffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  // Standard base64 → url-safe (per RFC 7515 §2)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
